<script setup lang="ts">
import type { GateDecision } from '@/api'

/**
 * The space a decision card closes up once it has faded, so what sits below slides up: shared by the gate card
 * (GateDecisionCard) and the chat tool approval card, so the collapse lives in one place. `decided` is the
 * decision the card inside has just received (ZhDecisionCard's `decided`): the card fades over
 * --motion-decision, then this space closes over GATE_CARD_CLOSE_MS, so a page keeps it for MOTION.decisionMs
 * plus GATE_CARD_CLOSE_MS before taking it off.
 */
defineProps<{ decided?: GateDecision }>()
</script>

<template>
	<div class="card-close-space" :data-decided="decided">
		<div class="card-close-space__inner">
			<slot />
		</div>
	</div>
</template>

<style scoped>
/*
 * The card sits in a one-row grid so its space can close from its full height to none once it has faded:
 * GATE_CARD_CLOSE_MS (250ms), after --motion-decision. Its margin in a stacked list closes with it. With
 * reduced motion the space goes in one step, at the same moment. It fills its row, a timeline step's included.
 */
.card-close-space { display: grid; grid-template-rows: 1fr; inline-size: 100%; }
.card-close-space__inner { min-height: 0; }
.card-close-space[data-decided] {
	grid-template-rows: 0fr;
	margin-block: 0;
	transition:
		grid-template-rows 0s linear var(--motion-decision),
		margin-block 0s linear var(--motion-decision);
}
@media (prefers-reduced-motion: no-preference) {
	.card-close-space[data-decided] {
		transition:
			grid-template-rows 250ms ease-out var(--motion-decision),
			margin-block 250ms ease-out var(--motion-decision);
	}
}
</style>
